import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext.tsx';
import { useCart } from '../contexts/CartContext.tsx';
import { formatMoney, pluralize } from '../lib/format.ts';

export function CartPage() {
  const navigate = useNavigate();
  const { snapshot, slug } = useTenant();
  const { lines, subtotal, count, updateQty, removeLine, clearCart } = useCart();

  const currency = snapshot.tenant.currency;
  const accentColor = snapshot.tenant.theme.accent;

  if (count === 0) {
    return (
      <div className="flex min-h-dvh flex-col" style={{ background: 'var(--bg)' }}>
        <PageHeader title="Корзина" onBack={() => navigate(`/s/${slug}`)} />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <span className="text-5xl" aria-hidden="true">🛒</span>
          <p className="text-lg font-medium" style={{ color: 'var(--text)' }}>Корзина пуста</p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Добавьте что-нибудь из меню
          </p>
          <button
            onClick={() => navigate(`/s/${slug}`)}
            className="mt-2 rounded-xl px-6 py-3 text-sm font-semibold"
            style={{ background: accentColor, color: '#fff' }}
          >
            К меню
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-dvh flex-col"
      style={{ background: 'var(--bg)', paddingBottom: 100 }}
    >
      <PageHeader title="Корзина" onBack={() => navigate(`/s/${slug}`)} />

      <main className="flex-1 px-4 pt-4">
        <p className="mb-3 text-sm" style={{ color: 'var(--text-muted)' }}>
          {pluralize(count, ['позиция', 'позиции', 'позиций'])}
        </p>

        <ul className="flex flex-col gap-3" aria-label="Позиции корзины">
          {lines.map((line) => (
            <li
              key={line.line_id}
              className="flex gap-3 rounded-xl p-3"
              style={{ background: 'var(--surface)', boxShadow: 'var(--shadow)' }}
            >
              {/* Image */}
              {line.image_path && (
                <img
                  src={`/tenants/${line.image_path}`}
                  alt=""
                  aria-hidden="true"
                  className="h-16 w-16 flex-shrink-0 rounded-lg object-cover"
                />
              )}

              <div className="flex flex-1 flex-col gap-1.5 min-w-0">
                <p className="truncate text-sm font-semibold" style={{ color: 'var(--text)' }}>
                  {line.item_name}
                </p>

                {/* Selected modifiers */}
                {line.selected.length > 0 && (
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                    {line.selected.map((s) => s.option_name).join(', ')}
                  </p>
                )}

                {/* Note */}
                {line.note && (
                  <p className="text-xs italic" style={{ color: 'var(--text-subtle)' }}>
                    {line.note}
                  </p>
                )}

                <div className="mt-auto flex items-center justify-between gap-2">
                  {/* Quantity stepper */}
                  <div
                    className="flex items-center rounded-lg"
                    style={{ border: '1px solid var(--border)' }}
                  >
                    <button
                      onClick={() => updateQty(line.line_id, -1)}
                      aria-label="Уменьшить"
                      className="flex h-8 w-8 items-center justify-center text-base"
                      style={{ color: 'var(--text)' }}
                    >
                      −
                    </button>
                    <span
                      className="w-6 text-center text-sm font-semibold tabular-nums"
                      style={{ color: 'var(--text)' }}
                      aria-live="polite"
                    >
                      {line.quantity}
                    </span>
                    <button
                      onClick={() => updateQty(line.line_id, 1)}
                      aria-label="Увеличить"
                      className="flex h-8 w-8 items-center justify-center text-base"
                      style={{ color: 'var(--text)' }}
                    >
                      +
                    </button>
                  </div>

                  <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--text)' }}>
                    {formatMoney(line.estimated_unit_price * line.quantity, currency)}
                  </span>
                </div>
              </div>

              {/* Remove button */}
              <button
                onClick={() => removeLine(line.line_id)}
                aria-label={`Удалить ${line.item_name}`}
                className="flex-shrink-0 self-start rounded-lg p-1.5"
                style={{ color: 'var(--text-muted)' }}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>

        {/* Summary */}
        <div
          className="mt-4 rounded-xl p-4"
          style={{ background: 'var(--surface)', boxShadow: 'var(--shadow)' }}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Итого</span>
            <span className="text-lg font-bold tabular-nums" style={{ color: 'var(--text)' }}>
              {formatMoney(subtotal, currency)}
            </span>
          </div>
        </div>
      </main>

      {/* Footer actions */}
      <div
        className="fixed bottom-0 left-0 right-0 flex flex-col gap-2 border-t p-3"
        style={{
          background: 'var(--surface)',
          borderColor: 'var(--border)',
          paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        }}
      >
        <button
          onClick={() => navigate(`/s/${slug}/checkout`)}
          className="flex h-12 w-full items-center justify-center rounded-xl text-base font-semibold"
          style={{ background: accentColor, color: '#fff' }}
        >
          Оформить заказ
        </button>
        <button
          onClick={clearCart}
          className="flex h-10 w-full items-center justify-center rounded-xl text-sm"
          style={{ color: 'var(--text-muted)' }}
        >
          Очистить корзину
        </button>
      </div>
    </div>
  );
}

function PageHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <header
      className="sticky top-0 z-20 flex items-center gap-3 px-2 py-3"
      style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' }}
    >
      <button
        onClick={onBack}
        aria-label="Назад"
        className="hit-target rounded-lg"
        style={{ color: 'var(--text)' }}
      >
        ←
      </button>
      <h1 className="text-base font-semibold" style={{ color: 'var(--text)' }}>
        {title}
      </h1>
    </header>
  );
}
